/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import {
  useState,
  useEffect,
  useRef,
  Fragment,
  useContext,
  MouseEvent,
  TouchEvent,
  useMemo,
  useCallback,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Tree, Modal, message } from 'antd';
import { useIntl } from 'react-intl';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { DataNode, DirectoryTreeProps, EventDataNode } from 'antd/lib/tree';

import IconButton from '~components/_common/IconButton/IconButton';
import navigationItems, {
  navigationItemsTimeMachine,
  getModuleNavigationItems,
} from '~src/data/navigationItems';
import { getParentsIds } from '~src/utils/tree';
import { DbFolder, DbTenderFolder, DbTender } from '~src/types/db';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { useProject } from '~src/hooks/api-hooks/useProject';
import { useIsModuleFileAdmin } from '~src/hooks/useIsModuleFileAdmin';
import { useSectorTree } from '~src/hooks/api-hooks/useSectorTree';
import { Block, Subdivision, Sector } from '~src/services/api/modules';
import LayerCustom from '~src/assets/nav-icons/layer-custom.svg';

import dataContextMenu from '~src/data/dataContextMenu';
import ShareModal from '~components/popups-modals/ShareModal/ShareModal';
import { FolderPropertiesModal } from '~components/popups-modals/FolderPropertiesModal/FolderPropertiesModal';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { EditFolderModal } from '~components/popups-modals/EditFolder.modal';
import { MoveModal } from '~components/popups-modals/Move.modal';
import { DeleteModal } from '~components/popups-modals/Delete.modal';
import { NavIcon } from '~components/_common/NavIcon/NavIcon';
import { downloadFolder } from '~src/utils/download';
import { appLog } from '~src/utils/log';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { RightsModal } from '~components/popups-modals/RightsModal/RightsModal';
import { useToggleFavouriteFolder } from '~src/hooks/actions/useToggleFavouriteFolder';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import { useItemPermission } from '~src/hooks/useItemPermission';
import { useProjectId } from '~src/hooks/useProjectId';

import { useIsPath } from '~src/hooks/useIsPath';

import { MODULES } from '~src/types/enums';
import ProjectSrc from '~src/assets/nav-icons/project.png';
import { useModules } from '~src/hooks/api-hooks/useModules';
import { ItemLogModal } from '~components/popups-modals/ItemLog.modal';
import _ from 'lodash';
import { useToggleFocusFolder } from '~src/hooks/actions/useToggleFocusFolder';
import { focusOptions } from '~components/atoms/RightClickOverlay/data';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { useTenderFolders } from '~src/hooks/api-hooks/useTenderFolders';
import { useTenders } from '~src/hooks/api-hooks/useTenders';
import FolderTender from '~src/assets/nav-icons/folder-tender.svg';
import FileTender from '~src/assets/nav-icons/file-tender.svg';
import FolderTenderHammer from '~src/assets/nav-icons/folder-tender-hammer.svg';

import FileTenderHammer from '~src/assets/nav-icons/file-tender-hammer.svg';
import { queryCache, useMutation } from 'react-query';
import {
  LoadFoldersParams,
  convertToTender,
  loadTendersTenderFoldersFolders,
  loadTenderRegistartionSubFolders,
  loadTenderSubFolders,
  getPrimarySVFRender,
  getPrimarySVFRenderLocation,
} from '~src/services/api';
import { useTenderFoldersFoldersId } from '~src/hooks/api-hooks/useTenderFoldersFoldersId';
import { useTenderFoldersFolders } from '~src/hooks/api-hooks/useTenderFoldersFolders';
import { useThemeSwitcher } from 'react-css-theme-switcher';
import doReturnTheme from '~src/styles/themeEngine';
import SidebarContext from '~src/contexts/SidebarContext';
import { useIsOnStart } from '~src/hooks/useIsOnStart';
import { arrToTree, getTenderIconArichive } from '../utils';
import { useMinimizedTreeData } from '~src/hooks/utils/useMinimizedTreeData';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';

export const getTenderIcon = (option) => {
  const { tender_registration_id, is_tender_root_folder } = option;
  if (is_tender_root_folder && tender_registration_id) return FileTenderHammer;
  if (is_tender_root_folder && !tender_registration_id)
    return FolderTenderHammer;
  if (tender_registration_id) return FileTender;
  return FolderTender;
};

const { DirectoryTree } = Tree;

type StateDataNode = DataNode & {
  path?: string;
  children?: StateDataNode[];
  parentId?: string | null;
  favourite?: boolean;
  moduleId?: MODULES;
  isTender?: boolean;
  keyPath?: string | null;
  type?: string | null;
  isTenderRootFolder?: string | null;
  tenderRegistrationId?: string | null;
  firstElementId?: string;
  isUnassigned?: boolean;
  displayTitle?: string;
};

const ProjectLeftSideView: React.FC<Record<string, unknown>> = () => {
  const { isSidebarMinimized } = useContext(SidebarContext);
  const { setLargeFilesContext } = useContext(LargeFilesContext);
  const { fileVersionsContext } = useContext(FileVersionsContext);

  const styles = useStyles(styleFn);
  const { currentTheme } = useThemeSwitcher();
  const projectQuery = useProject();
  const theme = doReturnTheme(currentTheme);
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();
  const projectId = useProjectId();
  const currentFolder = useCurrentFolder();

  const tenderIdMatch = history?.location?.pathname?.match(/tenders\/(\d+)/);
  const tenderId = tenderIdMatch ? String(tenderIdMatch[1]) : '';

  const archiveSettings = useArchiveSettings();
  const isTenderingOn =
    archiveSettings.data?.resArchiveSettings?.is_tendering_on &&
    (projectQuery?.data?.is_tender_manager || projectQuery?.data?.is_superuser);

  // `module_admins` requires superuser-level access on the backend - never
  // fire it for non-superusers (e.g. Manager accounts), who would always
  // get a 403 and default to the old module nav anyway.
  const hasModuleFileAdmin = useIsModuleFileAdmin({
    enabled: !!projectQuery?.data?.is_superuser,
  });

  // State to track if sectors should be loaded (lazy loading)
  const [shouldLoadSectors, setShouldLoadSectors] = useState(false);

  // Fetch sector tree data for admin users (lazy loaded on first expand)
  const sectorTreeQuery = useSectorTree(String(projectId), {
    enabled: hasModuleFileAdmin && !!projectId && shouldLoadSectors,
  });

  const foldersQuery = useFolders({
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const tenderFoldersQuery = useTenderFolders({
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const sortedFoldersQuery = useMemo(
    () => foldersQuery.data?.sort((a, b) => a.name.localeCompare(b.name)),
    [foldersQuery.data]
  );

  const sortedTenderFoldersQuery = useMemo(
    () => tenderFoldersQuery.data?.sort((a, b) => a.name.localeCompare(b.name)),
    [tenderFoldersQuery.data]
  );

  const pathSegments = history.location.pathname.split('/').filter(Boolean);
  const lastSegment = pathSegments[pathSegments.length - 1];
  const penultimateSegment = pathSegments[pathSegments.length - 3];

  const isDocumentsPath =
    pathSegments.includes('documents') && !isNaN(Number(lastSegment));
  const isUnassignedDocumentsPage = pathSegments[2] === 'tender-folders';

  // --- for Desktop view on Mobiles: Navigation Helpers ---
  // Centralized Navigation Logic (Extracted from handleSelect)
  const triggerNavigation = (node: StateDataNode) => {
    if (location.pathname.includes('/projects')) {
      const clickedKey = String(node.key);

      if (clickedKey === 'primary-svf-viewer') {
        openPrimarySVFfile();
        return;
      }

      // Special handling for modules section
      if (
        clickedKey === 'modules' ||
        clickedKey.startsWith('block-') ||
        clickedKey.startsWith('subdivision-') ||
        clickedKey.startsWith('sector-')
      ) {
        // @ts-ignore
        const nodePath: string | undefined = node.path;

        // If it's the root 'modules' node with no path, just expand/collapse
        if (clickedKey === 'modules' && !nodePath) {
          return;
        }

        // If modules section children have a path, use it directly (already includes /projects/{projectId})
        if (nodePath) {
          history.push(nodePath);
          return;
        }

        // For division/subdivision nodes without path, just expand/collapse
        if (
          !nodePath &&
          (clickedKey.startsWith('block-') ||
            clickedKey.startsWith('subdivision-'))
        ) {
          return;
        }
      }

      let path = '';
      // @ts-ignore
      const nodePath: string | undefined = node.path;

      if (nodePath) {
        path = nodePath?.length > 1 ? nodePath : '';
      } else {
        path = `${
          'isTender' in node && node.isTender ? '/tenders/' : '/archive/'
        }${'keyPath' in node ? node?.keyPath : node?.key}`;
      }

      history.push(`/projects/${projectId}` + path);
    } else {
      // Logic for non-project pages (Home, etc)
      if (node.path) {
        history.push({ pathname: node.path });
      } else if (String(node.key).includes('home')) {
        history.push('/');
      }
    }
  };

  // Touch-Friendly Wrapper
  // This component detects taps manually and forces navigation
  const InteractiveWrapper = ({ node, children, style }: any) => {
    const isTouchMoveRef = useRef(false);

    return (
      <span
        style={style}
        // Stop AntD from hijacking the click
        onMouseDown={(e) => e.stopPropagation()}
        // Reset scroll tracker
        onTouchStart={(e) => {
          e.stopPropagation();
          isTouchMoveRef.current = false;
        }}
        // Detect scrolling
        onTouchMove={() => {
          isTouchMoveRef.current = true;
        }}
        // Handle TAP
        onTouchEnd={(e) => {
          if (!isTouchMoveRef.current) {
            e.stopPropagation();
            e.preventDefault(); // Prevent ghost clicks
            triggerNavigation(node);
          }
        }}
        // Handle CLICK (Fallback for mouse)
        onClick={(e) => {
          e.stopPropagation();
          triggerNavigation(node);
        }}>
        {children}
      </span>
    );
  };

  const foldersQueryResult = useTenderFoldersFoldersId(
    {
      tender_id: tenderId,
      root_folder_id: isDocumentsPath ? penultimateSegment : undefined,
      folder_id: isDocumentsPath ? penultimateSegment : undefined,
    },
    // Check for documents path and if the folder is assigned to tender
    { enabled: isDocumentsPath && !!tenderId }
  );

  const foldersQueryUnasignedResult = useTenderFoldersFolders(
    {
      root_folder_id: penultimateSegment,
    },
    { enabled: isUnassignedDocumentsPage }
  );

  const [doGetTenderSubFolders, {}] = useMutation(loadTenderSubFolders, {
    onSuccess: (data) => {
      // queryCache.invalidateQueries('folders');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
    },
  });

  const [doGetTenderFolder, {}] = useMutation(
    async (params: LoadFoldersParams) => {
      const key = '';
      return loadTendersTenderFoldersFolders(key, params);
    },
    {
      onSuccess: (data) => {
        // queryCache.invalidateQueries('folders');
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      },
    }
  );

  const [doGetTenderFoldersFolders, {}] = useMutation(
    async (params: LoadFoldersParams) => {
      return loadTenderSubFolders(params);
    },
    {
      onSuccess: (data) => {
        // queryCache.invalidateQueries('folders');
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      },
    }
  );

  const [doGetTenderRegistrationSubFolders, {}] = useMutation(
    loadTenderRegistartionSubFolders,
    {
      onSuccess: (data) => {
        // queryCache.invalidateQueries('folders');
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      },
    }
  );

  const loadData = async (treeNode: StateDataNode): Promise<void> => {
    if (
      treeNode.type === 'archive' ||
      !treeNode.type ||
      treeNode.firstElementId !== undefined
    )
      return;
    const id = treeNode?.keyPath;
    // Check if the node is a tender
    if (treeNode?.key === 'tenders') {
      // Fetch the tenderFolders and tenderRegistrations
      /*   const tenderFolders = tenderFoldersQuery.data || [];
    const tenderRegistrations = tendersQuery.data || [];

    // Convert the fetched data into the format required by the tree
    const tenderFoldersTreeData = arrToTreeTenderFolder(tenderFolders, null);
    const tenderRegistrationsTreeData = arrToTreeTenders(
      tenderRegistrations,
      null,
      tenderFolders
    );
    */
      // Set the children of the tender node
      /*     treeNode.children = [+
      ...tenderRegistrationsTreeData,
      ...tenderFoldersTreeData,
    ]; */
    } else if (treeNode?.key === 'projectweb') {
      //history.push(`/`);
    } else {
      // If the node is not a tender, fetch the subfolders
      let subFolders;
      let subFoldersTreeData;

      // Extract tender ID from treeNode instead of relying on URL
      const effectiveTenderId = treeNode?.tenderRegistrationId
        ? String(treeNode.tenderRegistrationId)
        : treeNode?.keyPath && treeNode.type === 'tender_registartions'
        ? String(treeNode.keyPath)
        : tenderId;

      if (treeNode?.isTenderRootFolder) {
        subFolders =
          (await doGetTenderFolder({
            projectId: String(projectId),
            root_folder_id: String(treeNode?.tenderRegistrationId),
            folder_id: String(id),
          })) || [];

        // Перетворюємо у масив, бо бек може повернути або масив, або обʼєкт
        const subFoldersArray = Array.isArray(subFolders)
          ? subFolders
          : Object.values(subFolders || {});

        // Шукаємо справжній root (parent_id === null), fallback — перший елемент
        const rootFolder =
          subFoldersArray.find((item) => item.parent_id === null) ||
          subFoldersArray[0] ||
          null;

        // Стартуємо рекурсію з rootFolder як "parent" —
        // тобто одразу беремо його дітей (B02 Rådgiver, test2 і т.д.),
        // а не дублюємо сам root ще раз у children
        subFoldersTreeData = arrToTreeTenderFolderSubNoLoad(
          subFolders,
          rootFolder,
          effectiveTenderId,
          rootFolder ? String(rootFolder.id) : undefined
        );

        treeNode.children = subFoldersTreeData;
      } else if (treeNode?.isUnassigned) {
        subFolders =
          (await doGetTenderFoldersFolders({
            projectId: String(projectId),
            root_folder_id: String(treeNode?.keyPath),
            // folder_id: String(id),
          })) || [];
        subFoldersTreeData = arrToTreeTenderFolderSubUnnasignedNoLoad(
          subFolders,
          subFolders[0],
          tenderId,
          subFolders[0]?.id
        );
        treeNode.children = [...subFoldersTreeData];
      } else if (treeNode.type === 'tender-folder') {
        subFolders =
          (await doGetTenderSubFolders({
            projectId: String(projectId),
            root_folder_id: String(id),
          })) || [];
        subFoldersTreeData = arrToTreeTendersSub(
          subFolders,
          null,
          effectiveTenderId
        );
        treeNode.children = [...subFoldersTreeData];
      } else {
        subFolders =
          (await doGetTenderRegistrationSubFolders({
            projectId: String(projectId),
            root_folder_id: String(id),
          })) || [];
        subFoldersTreeData = arrToTreeTendersSub(
          subFolders,
          null,
          effectiveTenderId
        );
        treeNode.children = [...subFoldersTreeData];
      }
    }

    // Cache loaded children so the tree rebuild can restore them after sidebar toggles
    if (treeNode.children !== undefined) {
      loadedChildrenRef.current.set(String(treeNode.key), treeNode.children);
    }

    // Find the corresponding node in treeData
    const nodeInTreeData = findNodeInTreeData(String(treeNode?.key), treeData);

    if (nodeInTreeData) {
      nodeInTreeData.children = treeNode.children;

      // Trigger a re-render by setting the state
      setTreeData((prevTreeData) => {
        return prevTreeData.map((node) =>
          node.key === treeNode.key ? treeNode : node
        );
      });
    }
  };

  const findNodeInTreeData = (
    key: string,
    treeData: StateDataNode[]
  ): StateDataNode | null => {
    for (const node of treeData) {
      if (node.key === key) {
        return node;
      }

      if (node.children) {
        const foundNode = findNodeInTreeData(key, node.children);
        if (foundNode) {
          return foundNode;
        }
      }
    }

    return null;
  };

  const tendersQuery = useTenders({
    before:
      window.sessionStorage.getItem('timeMachineDate') !== null
        ? Number(window.sessionStorage.getItem('timeMachineDate'))
        : undefined,
    as_admin:
      window.sessionStorage.getItem('appState') === 'superUser'
        ? true
        : undefined,
  });

  const isOnArchive = useIsPath('/archive');
  const isOnModule = useIsPath('/module');
  const isOnTenders = useIsPath('/tenders');
  const isOnTenderFolders = useIsPath('/tender-folders');
  const isOnDocuments = useIsPath('/documents', { endsWith: true });
  const isOnDetails = useIsPath('/details', { endsWith: true });
  const isOnDashboard = useIsPath('/dashboard', { endsWith: true });
  const isOnContract = useIsPath('/contract', { endsWith: true });
  const isOnMovements = useIsPath('/movements');
  const isOnSectors = useIsPath('/section-modules/sector');
  const isOnProjectWeb = useIsOnStart();
  const isProjectDetailsPage = useIsPath('projects');

  const modules = useModules();

  const { toggleFavouriteFolder } = useToggleFavouriteFolder();
  const { toggleFocusFolder } = useToggleFocusFolder();
  const { setPrimaryPreviewFile, setFileToView } = useFileViewerContext();

  const [doConvertToTender, {}] = useMutation(convertToTender, {
    onSuccess: () => {
      queryCache.invalidateQueries('folders');
      foldersQuery.refetch();
      message.success(intl.formatMessage({ id: 'folder-to-tender-success' }));
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'folder-to-tender-failed' }));
      appLog('ConvertToTender -> convertTender -> item', item);
      appLog('ConvertToTender -> convertTender -> err', error);
    },
  });

  // state for all modals
  const [shareModal, setShareModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [folderEditModal, setFolderEditModal] = useState(false);
  const [folderMoveModal, setFolderMoveModal] = useState(false);
  const [folderPropertiesModal, setFolderPropertiesModal] = useState(false);
  const [folderRightsModal, setFolderRightsModal] = useState(false);
  const [itemLogModal, setItemLogModal] = useState(false);

  const [rightClickFolder, setRightClickFolder] = useState<DbFolder>();
  const [
    rightClickFolderOverlay,
    setRightClickFolderOverlay,
  ] = useState<DbFolder>();

  const rightClickFolderParentPermission = useItemPermission({
    folder: rightClickFolderOverlay,
  });

  const errorModalRef = useRef<ReturnType<typeof Modal.error>>();
  const loadedChildrenRef = useRef<Map<string, StateDataNode[]>>(new Map());

  // states
  const [treeData, setTreeData] = useState<StateDataNode[]>([
    {
      title: '',
      path: '/',
      key: 'home',
      keyPath: 'home',
      icon: <NavIcon icon={ProjectSrc} />,
      parentId: null,
      children: [],
    },
  ]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [expandedKeys, setExpandedKeys] = useState<string[]>(
    isProjectDetailsPage ? ['home'] : ['']
  );

  useEffect(() => {
    if (modules.data && isProjectDetailsPage) {
      setTreeData((prevState) => {
        // Fix for Race Condition: Preserve existing children
        const existingChildrenMap: Record<string, StateDataNode[]> = {};
        if (prevState[0] && prevState[0].children) {
          prevState[0].children.forEach((child) => {
            if (child.key && child.children && child.children.length > 0) {
              existingChildrenMap[child.key] = child.children;
            }
          });
        }

        const nextState = [...prevState];
        if (nextState[0]) {
          // Get all base navigation items (project-data section)
          const baseNavItems = (window.sessionStorage.getItem('appState') ===
          'timeMachine'
            ? navigationItemsTimeMachine
            : navigationItems
          ).filter((v) => v.section !== 'special');

          // Get module navigation items based on permission
          const moduleNavItems = getModuleNavigationItems(hasModuleFileAdmin);

          // Combine base and module items, then filter
          nextState[0].children = [...baseNavItems, ...moduleNavItems]
            .filter(
              (v) =>
                // For special section items (modules/sectors), check if module is enabled
                (v.section !== 'special' ||
                  v.key === 'modules' || // Always show modules if it's in the list (permission checked above)
                  (modules.data &&
                    v?.moduleName &&
                    modules.data[v.moduleName])) &&
                v.key !== 'shared-with-me' &&
                (v.key !== 'tenders' || (v.key === 'tenders' && isTenderingOn)) // include 'tender' when isTenderingOn is true
            )
            .map((navItem) => {
              // Restore existing children if available
              const childrenToRestore = existingChildrenMap[navItem.key] || [];

              // Construct the node object explicitly so we can pass it to the wrapper
              const nodeObj = {
                title: intl.formatMessage({ id: navItem.nameTerm }),
                path: navItem.path,
                key: navItem.key,
                moduleId: navItem.id,
                parentId: null,
              };

              return {
                ...nodeObj,
                isLeaf: childrenToRestore.length > 0 ? false : navItem.isLeaf,
                children: childrenToRestore,
                // WRAP THE ICON IN INTERACTIVE WRAPPER
                icon: (
                  <InteractiveWrapper node={nodeObj} style={{ height: '100%' }}>
                    {navItem.section === 'project-data' ? (
                      navItem.key === 'tenders' ? (
                        <NavIcon
                          customStyle={{
                            height: '75%',
                            marginLeft: '-8px',
                            width: '120%',
                          }}
                          icon={navItem.customIcon as string}
                        />
                      ) : navItem.key === 'primary-svf-viewer' ? (
                        <IconButton
                          icon={navItem.icon as IconProp}
                          color="red"
                          customStyle={[styles.icons, styles.iconRed]}
                          backgroundOff
                        />
                      ) : (
                        <IconButton
                          icon={navItem.icon as IconProp}
                          color={navItem.color}
                          customStyle={styles.icons}
                          backgroundOff
                        />
                      )
                    ) : navItem.key === 'modules' ? (
                      <IconButton
                        icon={navItem.icon as IconProp}
                        color={navItem.color}
                        customStyle={styles.icons}
                        backgroundOff
                      />
                    ) : (
                      <NavIcon icon={navItem.customIcon as string} />
                    )}
                  </InteractiveWrapper>
                ),
              };
            });
        }
        return nextState;
      });
    }
  }, [modules.data, isTenderingOn, isProjectDetailsPage, hasModuleFileAdmin]);

  useEffect(() => {
    if (foldersQuery.error) {
      const modalContent =
        intl.formatMessage({ id: 'load-folders-error' }) +
        (foldersQuery.error.code ? ` (${foldersQuery.error.code})` : '');

      if (!errorModalRef.current) {
        errorModalRef.current = Modal.error({
          title: intl.formatMessage({ id: 'generic-error' }),
          content: modalContent,
          keyboard: false, // disable close on ESC
          okButtonProps: { loading: foldersQuery.isLoading },
          okText: intl.formatMessage({ id: 'try-again' }),
          onOk: () => foldersQuery.refetch({ throwOnError: true }),
        });
      } else {
        errorModalRef.current.update({
          content: modalContent,
          okButtonProps: { loading: foldersQuery.isLoading },
        });
      }
    } else if (errorModalRef.current) {
      errorModalRef.current.destroy();
      errorModalRef.current = undefined;
    }
  }, [foldersQuery, intl]);

  useEffect(() => {
    if (projectQuery.data && isProjectDetailsPage) {
      setTreeData((prevState) => {
        const nextState = [...prevState];
        if (nextState[0]) {
          nextState[0].title =
            projectQuery.data?.name || intl.formatMessage({ id: 'home' });
        }
        return nextState;
      });
    }
  }, [projectQuery.data, isProjectDetailsPage]);

  /** set selected item based on location */
  useEffect(() => {
    const locationList = location.pathname.split('/').filter(Boolean);

    // if folder location
    if (locationList.length > 3 && sortedFoldersQuery) {
      if (isOnArchive) {
        const folderId = locationList[3];

        setExpandedKeys((prevExpandedKeys) => {
          if (prevExpandedKeys.includes(folderId)) {
            return [...prevExpandedKeys];
          }

          const parents = getParentsIds(sortedFoldersQuery || [], folderId);

          return [...prevExpandedKeys, 'archive', ...parents, folderId];
        });

        setSelectedKeys((prevSelectedKeys) =>
          prevSelectedKeys[0] === folderId ? [...prevSelectedKeys] : [folderId]
        );

        return;
      }
      if (isOnTenders || isOnTenderFolders) {
        let folderId: string;
        let parents: string[] = [];
        if (
          isOnContract ||
          isOnMovements ||
          isOnDetails ||
          isOnDashboard ||
          isOnDocuments
        ) {
          folderId =
            locationList[locationList.length - 2] + '_tender_registartions';
        } else if (locationList[2] === 'tender-folders') {
          folderId = locationList[locationList.length - 1] + '_tender_folders';
          if (
            locationList[locationList.length - 1] !==
            locationList[locationList.length - 3]
          ) {
            parents = [
              `${locationList[locationList.length - 3]}_tender_folders`,
            ];
          }
        } else if (
          locationList[locationList.length - 2] === 'folders' &&
          locationList[locationList.length - 1] !==
            locationList[locationList.length - 3]
        ) {
          folderId = locationList[locationList.length - 1] + '_tender_folders';
          if (
            locationList[locationList.length - 2] === 'folders' &&
            locationList[locationList.length - 1] !==
              locationList[locationList.length - 3]
          ) {
            parents = [
              `${tenderId}_tender_registartions`,
              `${locationList[locationList.length - 3]}_tender_registartions`,
            ];
          }
        } else if (locationList[locationList.length - 2] === 'folders') {
          folderId =
            locationList[locationList.length - 1] + '_tender_registartions';
          parents = [`${tenderId}_tender_registartions`];
        } else {
          folderId =
            locationList[locationList.length - 1] + '_tender_registartions';
        }

        setExpandedKeys((prevExpandedKeys) => {
          const parentsTenderNoLoadArray = getParentsIds(
            foldersQueryResult.data || foldersQueryUnasignedResult.data || [],
            locationList[locationList.length - 1]
          );
          const parentsTenderNoLoad = parentsTenderNoLoadArray
            .slice(1)
            .map((id) => `${id}_tender_folders`);
          if (
            prevExpandedKeys.includes(folderId) &&
            parentsTenderNoLoad.length === 0
          ) {
            return [...prevExpandedKeys];
          }

          return [
            ...prevExpandedKeys,
            // 'tender',
            'tenders',
            ...parents,
            ...parentsTenderNoLoad,
            folderId,
          ];
        });

        setSelectedKeys((prevSelectedKeys) =>
          prevSelectedKeys[0] === folderId ? [...prevSelectedKeys] : [folderId]
        );

        return;
      }

      if (isOnModule) {
        const moduleId = locationList[3];
        const matchingItemIndex = (treeData[0]
          .children as StateDataNode[]).findIndex(
          (item) => Number(moduleId) === item?.moduleId
        );
        const matchingItem = (treeData[0].children as StateDataNode[])[
          matchingItemIndex
        ];
        if (matchingItem) {
          setExpandedKeys((prevExpandedKeys) =>
            prevExpandedKeys.includes(matchingItem.key as string)
              ? [...prevExpandedKeys]
              : [...prevExpandedKeys, matchingItem.key as string]
          );

          setSelectedKeys((prevSelectedKeys) =>
            prevSelectedKeys[0] && prevSelectedKeys[0] === matchingItem.key
              ? [...prevSelectedKeys]
              : [matchingItem.key as string]
          );
        }
        return;
      }
    }

    // Handle sectors (modules sections) - separate from folder query logic
    if (isOnSectors && locationList.length >= 6) {
      // URL structure: /projects/{projectId}/section-modules/sector/{sectorId|outside}/registrations
      // locationList[4] contains the sectorId or "outside"
      const sectorIdentifier = locationList[4]; // e.g., "123" or "outside"

      if (!sectorIdentifier) {
        return; // Exit early if no identifier found
      }

      // Construct the tree key based on the identifier
      const sectorKey =
        sectorIdentifier === 'null'
          ? 'sector-outside'
          : `sector-${sectorIdentifier}`;

      // Find the sector node and its parents in the tree
      const findSectorPath = (
        nodes: StateDataNode[],
        targetKey: string
      ): string[] | null => {
        for (const node of nodes) {
          if (node.key === targetKey) {
            return [targetKey];
          }
          if (node.children) {
            const childPath = findSectorPath(node.children, targetKey);
            if (childPath) {
              return [node.key as string, ...childPath];
            }
          }
        }
        return null;
      };

      // Get the modules node
      const modulesNode = treeData[0]?.children?.find(
        (c) => c.key === 'modules'
      );
      if (modulesNode?.children) {
        const sectorPath = findSectorPath(modulesNode.children, sectorKey);

        if (sectorPath) {
          // Expand all parent nodes (modules, division, subdivision) and the sector itself
          setExpandedKeys((prevExpandedKeys) => {
            const newKeys = ['modules', ...sectorPath];
            const uniqueKeys = [...new Set([...prevExpandedKeys, ...newKeys])];
            return uniqueKeys;
          });

          // Highlight the sector
          setSelectedKeys((prevSelectedKeys) =>
            prevSelectedKeys[0] === sectorKey
              ? [...prevSelectedKeys]
              : [sectorKey]
          );
        }
      }

      return; // Exit after handling sector highlighting
    }

    if (locationList.length > 1) {
      // if level 1 location e.g. archive, recent etc.
      // if no lv1 location, match treeData[0] = 'home'
      const matchingItem = locationList[2]
        ? [...treeData, ...(treeData[0].children as StateDataNode[])].find(
            (v) => v.path === '/' + locationList[2]
          )
        : treeData[0];

      if (matchingItem) {
        setExpandedKeys((prevExpandedKeys) =>
          prevExpandedKeys.includes(matchingItem.key as string)
            ? [...prevExpandedKeys]
            : [...prevExpandedKeys, matchingItem.key as string]
        );

        setSelectedKeys((prevSelectedKeys) =>
          prevSelectedKeys[0] === matchingItem.key
            ? [...prevSelectedKeys]
            : [matchingItem.key as string]
        );
      }
    }
  }, [
    location.pathname,
    location.search,
    treeData,
    sortedFoldersQuery,
    foldersQueryResult.data,
    isOnArchive,
    isOnModule,
    isOnSectors,
    isOnContract,
    isOnMovements,
    isOnDetails,
    isOnDashboard,
    isOnDocuments,
    isOnTenders,
    isOnTenderFolders,
  ]);

  // Recursively restores children that were lazily loaded via loadData.
  // The loadedChildrenRef map is keyed by node key and is populated in loadData.
  // Priority: cached (lazily loaded) children > pre-built children from API data.
  const restoreLoadedChildren = (nodes: StateDataNode[]): StateDataNode[] =>
    nodes.map((node) => {
      const cached = loadedChildrenRef.current.get(String(node.key));
      if (cached !== undefined) {
        return { ...node, children: restoreLoadedChildren(cached) };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: restoreLoadedChildren(node.children) };
      }
      return node;
    });

  useEffect(() => {
    if (sortedFoldersQuery && !isOnProjectWeb) {
      const archiveChildren = arrToTree(
        sortedFoldersQuery,
        null,
        theme,
        focusOptions,
        currentFolder
      );

      setTreeData((prevTreeData) => {
        const nextTreeData = [...prevTreeData];
        // Fix: Find by key instead of hardcoded index [1]
        if (nextTreeData[0] && nextTreeData[0].children) {
          const archiveIndex = nextTreeData[0].children.findIndex(
            (c) => c.key === 'archive'
          );
          if (archiveIndex > -1) {
            nextTreeData[0].children[archiveIndex].children = isSidebarMinimized
              ? []
              : archiveChildren;
          }
        }
        return nextTreeData;
      });
    }
    if (sortedTenderFoldersQuery || tendersQuery.data) {
      const tenderFolder = sortedTenderFoldersQuery || [];
      const tenders = tendersQuery.data || [];
      const tenderFoldersChildren = arrToTreeTenderFolder(
        tenderFolder.filter((item) => !item?.options.tender_registration_id),
        null
      );

      const tendersChildren = arrToTreeTenders(tenders, null, tenderFolder);

      setTreeData((prevTreeData) => {
        const nextTreeData = [...prevTreeData];

        // Fix: Find by key instead of hardcoded index [2]
        if (nextTreeData[0]?.children) {
          const tendersIndex = nextTreeData[0].children.findIndex(
            (c) => c.key === 'tenders'
          );
          if (tendersIndex > -1) {
            const freshTenderChildren = [
              ...tendersChildren,
              ...tenderFoldersChildren,
            ];

            nextTreeData[0].children[
              tendersIndex
            ].children = restoreLoadedChildren(freshTenderChildren);
          }
        }

        return nextTreeData;
      });
    }
  }, [
    sortedFoldersQuery,
    sortedTenderFoldersQuery,
    tendersQuery.data,
    modules.data,
    isSidebarMinimized,
    // currentFolder,
  ]);

  // Populate modules tree for admin users
  useEffect(() => {
    if (sectorTreeQuery.data && hasModuleFileAdmin && projectId) {
      // Custom icon style for modules - smaller fontSize but same margins as global icons
      const modulesIconStyle = {
        fontSize: '18px',
        transition: '0.3s',
        margin: '3px 0 10px -16px',
      };

      const modulesChildren = convertSectorTreeToDataNodes(
        sectorTreeQuery.data,
        intl,
        projectId,
        modulesIconStyle
      );

      setTreeData((prevTreeData) => {
        const nextTreeData = [...prevTreeData];

        // Find the modules node and populate its children
        if (nextTreeData[0]?.children) {
          const modulesIndex = nextTreeData[0].children.findIndex(
            (c) => c.key === 'modules'
          );
          if (modulesIndex > -1) {
            nextTreeData[0].children[modulesIndex].children = modulesChildren;
          }
        }

        return nextTreeData;
      });
    }
  }, [sectorTreeQuery.data, hasModuleFileAdmin, intl, projectId]);

  // recolor for 3d previewer
  useEffect(() => {
    setTreeData((prev) => {
      if (!prev[0]?.children?.length) return prev;

      const hasPrimarySVF = !!archiveSettings?.data?.resArchiveSettings
        ?.primary_svf_render_id;
      const next = [...prev];
      const root = { ...next[0] };

      root.children = root.children?.map((child) => {
        if (child.key !== 'primary-svf-viewer') return child;
        return {
          ...child,
          icon: (
            <IconButton
              icon={
                navigationItems.find((i) => i.key === 'primary-svf-viewer')
                  ?.icon as IconProp
              }
              color={hasPrimarySVF ? undefined : 'red'}
              customStyle={[
                styles.icons,
                hasPrimarySVF ? styles.grayBlue : styles.iconRed,
              ]}
              backgroundOff
            />
          ),
        };
      });

      next[0] = root;
      return next;
    });
  }, [archiveSettings.data, styles.icons, styles.grayBlue, styles.iconRed]);

  // preview 3d file func
  const openPrimarySVFfile = useCallback(async () => {
    if (!projectId) return;
    try {
      if (!archiveSettings?.data?.resArchiveSettings?.primary_svf_render_id) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      const meta = await getPrimarySVFRender(Number(projectId));
      if (!meta) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      const loc = await getPrimarySVFRenderLocation(Number(projectId));
      if (!meta?.file_guid || !loc?.urn || !loc?.access_token) {
        message.error(
          intl.formatMessage({ id: 'primary-svf-file-preview-error-null' })
        );
        return;
      }

      setFileToView(undefined);
      setPrimaryPreviewFile({
        id: meta.file_guid || 0,
        title: meta.versioned_file_title,
        folder_id: meta.versioned_file_folder_id,
        serial_number: meta.file_serial_number,
        autodesk_object_id: meta.autodesk_object_id,
        urn: loc.urn,
        access_token: loc.access_token,
      } as any);
    } catch (err) {
      appLog('[ProjectLeftSideView] openPrimarySVFfile error', err);
    }
  }, [projectId, setPrimaryPreviewFile, setFileToView]);

  /** Item click handler */
  const handleSelect: DirectoryTreeProps['onSelect'] = (
    keys,
    info: {
      event: 'select';
      selected: boolean;
      node: EventDataNode | StateDataNode;
    }
  ) => {
    if (location.pathname.includes('/projects')) {
      let path = '';

      const clickedKey = String(info.node.key);
      if (clickedKey === 'primary-svf-viewer') {
        appLog(`pressed preview`);
        openPrimarySVFfile();
        return;
      }

      // Special handling for modules section
      if (
        clickedKey === 'modules' ||
        clickedKey.startsWith('block-') ||
        clickedKey.startsWith('subdivision-') ||
        clickedKey.startsWith('sector-')
      ) {
        // @ts-ignore-line
        const nodePath: string | undefined = info.node.path;

        // If it's the root 'modules' node with no path, just expand/collapse
        if (clickedKey === 'modules' && !nodePath) {
          return;
        }

        // If modules section children have a path, use it directly (already includes /projects/{projectId})
        if (nodePath) {
          history.push(nodePath);
          return;
        }

        // For division/subdivision nodes without path, just expand/collapse
        if (
          !nodePath &&
          (clickedKey.startsWith('block-') ||
            clickedKey.startsWith('subdivision-'))
        ) {
          return;
        }
      }

      // @ts-ignore-line
      const nodePath: string | undefined = info.node.path;
      if (nodePath) {
        // if nodePath='/', navigate to ''
        path = nodePath?.length > 1 ? nodePath : '';
      } else {
        path = `${
          'isTender' in info.node && info.node.isTender
            ? '/tenders/'
            : '/archive/'
        }${'keyPath' in info.node ? info.node?.keyPath : info.node?.key}`;
      }

      // setSelectedKeys is called in location change effect, triggered by history.push
      history.push(`/projects/${projectId}` + path);
    } else {
      if ('path' in info.node && String(info.node.key).includes('my-tenders')) {
        return history.push({
          pathname: info.node.path,
        });
      }
      if (
        'path' in info.node &&
        String(info.node.key).includes('invitations') &&
        info.node.path
      ) {
        return history.push({
          pathname: info.node.path,
          //search: info.node.path,
        });
      }
      if (
        'path' in info.node &&
        String(info.node.key).includes('home') &&
        info.node.path
      ) {
        return history.push({
          pathname: '/',
          //search: info.node.path,
        });
      }
    }
  };

  interface HandleRightClickProps {
    // info: {
    event:
      | MouseEvent<Element, globalThis.MouseEvent>
      | TouchEvent<HTMLSpanElement>;
    node: EventDataNode | StateDataNode;
    // }
  }
  const handleRightClick = ({ node, event }: HandleRightClickProps) => {
    if (navigationItems.some((item) => item.key === node.key)) {
      setRightClickFolder(undefined);
      setRightClickFolderOverlay(undefined);
    }
    const found = sortedFoldersQuery?.find(
      (folder) => String(folder.id) === node.key
    );

    setRightClickFolder(found ? { ...found } : undefined);
    setRightClickFolderOverlay(found ? { ...found } : undefined);

    requestAnimationFrame(() => {
      const syntheticEvent = new window.MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: (event as MouseEvent).clientX,
        clientY: (event as MouseEvent).clientY,
      });

      const treeElement = document.querySelector('.site-dropdown-context-menu');
      if (treeElement) {
        treeElement.dispatchEvent(syntheticEvent);
      }
    });
  };

  /** Toggle `is_favourite` prop, for selected folder */
  const toggleFavourite = async () => {
    if (!rightClickFolder) return;
    setTimeout(() => toggleFavouriteFolder(rightClickFolder), 100);
  };

  const rightClickAction = (modal: string, option?: number) => {
    const modalsMap = {
      share: setShareModal,
      'folder-edit': setFolderEditModal,
      'folder-move': setFolderMoveModal,
      'folder-properties': setFolderPropertiesModal,
      'rights-managment': setFolderRightsModal,
      'item-log': setItemLogModal,
      delete: setDeleteModal,
    };

    // set modal visible
    if (modalsMap[modal]) {
      modalsMap[modal](true);
    } else {
      // non-modal actions
      if (modal === 'folder-download') {
        if (!rightClickFolder) return;
        const hidePrepMsg = message.loading(
          intl.formatMessage({ id: 'download-preparing' }),
          0
        );
        return downloadFolder({
          folder: rightClickFolder,
          allFolders: sortedFoldersQuery || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            hidePrepMsg();
            setTimeout(
              () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
              400
            );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (modal === 'favourite') {
        toggleFavourite();
      }
      if (modal === 'folder-focus') {
        if (!rightClickFolder || !option) return;
        toggleFocusFolder(rightClickFolder, option);
      }
      if (modal === 'folder-tender') {
        if (!rightClickFolder) return;
        doConvertToTender({
          projectId: String(projectId),
          folderId: rightClickFolder.id,
        });
      }
    }
  };

  const timeOutRef = useRef<number>();
  const LONG_PRESS_TIME = 6 * 100;

  const adjustedTreeData = useMinimizedTreeData(
    isSidebarMinimized,
    treeData,
    treeData[0].children || []
  );

  return (
    <Fragment>
      <div
        css={styles.container}
        onContextMenu={(e) => e.preventDefault()}
        // onTouchStart={(e) => e.preventDefault()}
      >
        <RightClickOverlay
          focusOptions={focusOptions}
          leftSide={true}
          permissionLevel={rightClickFolder?.permission || 0}
          items={
            rightClickFolder
              ? window.sessionStorage.getItem('appState') === 'timeMachine'
                ? dataContextMenu.folder.filter(
                    (item) => item.name === 'download'
                  )
                : dataContextMenu.folder
              : []
          }
          onItemClick={(item, option) => {
            rightClickAction(item.modal, option);
            setTimeout(() => setRightClickFolderOverlay(undefined), 1000);
          }}
          onOverlayClose={() => {
            if (rightClickFolder)
              setTimeout(() => setRightClickFolderOverlay(undefined), 1000);
          }}
          selected={{ folder: rightClickFolder }}
          disabled={!rightClickFolder}>
          <DirectoryTree
            className={`
              ${
                window.sessionStorage.getItem('appState') !== 'timeMachine'
                  ? 'draggable-tree'
                  : 'timeMachine-tree'
              }
              ${isSidebarMinimized ? 'composited-tree' : ''}
            `}
            blockNode
            // onContextMenu={handleRightClick}
            treeData={adjustedTreeData}
            loadData={loadData}
            selectedKeys={selectedKeys}
            expandedKeys={expandedKeys}
            onSelect={handleSelect}
            onExpand={(keys) => {
              setExpandedKeys(keys as string[]);
              // Lazy load sectors when modules tree node is first expanded
              if (
                (keys as string[]).includes('modules') &&
                !shouldLoadSectors
              ) {
                setShouldLoadSectors(true);
              }
            }}
            onRightClick={({ event, node }) =>
              handleRightClick({ event, node })
            }
            onActiveChange={(key) => {
              appLog('key here -> ', key);
            }}
            titleRender={(node: StateDataNode) => (
              <InteractiveWrapper node={node}>
                {isSidebarMinimized ? null : node.displayTitle ?? node.title}
                {node.favourite && (
                  <IconButton
                    customStyle={{
                      fontSize: 12,
                      marginBottom: 1,
                    }}
                    containerStyle={{
                      height: 30,
                      width: 30,
                    }}
                    icon={['fas', 'star']}
                    color="yellow"
                    backgroundOff
                  />
                )}
              </InteractiveWrapper>
            )}
          />
        </RightClickOverlay>
      </div>

      {/* all modals */}
      <ShareModal state={shareModal} clicked={() => setShareModal(false)} />
      <DeleteModal
        visible={deleteModal}
        folders={rightClickFolder ? [rightClickFolder] : undefined}
        onCancel={() => setDeleteModal(false)}
      />
      <EditFolderModal
        visible={folderEditModal}
        folder={rightClickFolder}
        onCancel={() => setFolderEditModal(false)}
      />
      <MoveModal
        visible={folderMoveModal}
        folders={rightClickFolder ? [rightClickFolder] : undefined}
        folderId={rightClickFolder?.parent_id || null}
        onCancel={() => setFolderMoveModal(false)}
      />
      <RightsModal
        visible={folderRightsModal}
        onCancel={() => setFolderRightsModal(false)}
        folder={rightClickFolder}
      />
      <FolderPropertiesModal
        visible={folderPropertiesModal}
        onCancel={() => setFolderPropertiesModal(false)}
        folder={rightClickFolder}
      />
      <ItemLogModal
        visible={itemLogModal}
        onCancel={() => {
          setItemLogModal(false);
          setRightClickFolderOverlay(undefined);
        }}
        folder={rightClickFolder}
      />
    </Fragment>
  );
};

export default ProjectLeftSideView;

const styleFn: ThemedStyleFn = (theme) => ({
  container: {
    height: '100%',
    maxHeight: '100%',
    width: '100%',
    overflow: 'auto',
  },
  icons: {
    fontSize: '20px',
    transition: '0.3s',
    margin: '3px 0 10px -16px',
  },
  badge: {
    right: '-4px',
    bottom: '14px',
    color: 'black',
  },
  grayBlue: { color: '#184a9a' },
  iconRed: { color: theme.colors.red },
});

const dbTenderFolderToTreeObj = (
  dbTenderFolder: DbTenderFolder
): StateDataNode => {
  const { id, name, options } = dbTenderFolder;
  const {
    root_folder_id,
    is_tender_root_folder,
    tender_registration_id,
  } = options;

  return {
    title: name || 'Unknown',
    key: String(id) + '_tender_folders',
    keyPath: String(id),
    icon: (
      <NavIcon
        icon={getTenderIcon({
          tender_registration_id: tender_registration_id,
          is_tender_root_folder: is_tender_root_folder,
        })}
      />
    ),
    children: [],
    isTender: true,
    parentId: String(root_folder_id),
  };
};

/* const dbTendersToTreeObj = (dbTenderFolder: DbTenderFolder): StateDataNode => {
  const { id, title, tender_parent_id, registration_id } = dbTenderFolder;

  return {
    title: title || 'Unknown',
    key: String(id) + '_tender_folder',
    keyPath: String(id),
    icon: <NavIcon icon={FileTenderHammer} />,
    children: [],
    isTender: true,
    parentId: String(tender_parent_id),
  };
}; */

const dbTendersChildrenToTreeObj = (
  dbTenderFolder: DbTenderFolder | DbTender
): StateDataNode => {
  let idItem, root_folder_id;

  if ('id' in dbTenderFolder) {
    idItem = dbTenderFolder.id;
    root_folder_id = (dbTenderFolder as DbTenderFolder)?.options
      ?.root_folder_id;
  } else {
    idItem = (dbTenderFolder as DbTender).id;
    root_folder_id = undefined;
  }

  return {
    title: dbTenderFolder?.name || 'Unknown',
    key: String(idItem) + '_tender_folders_children',
    keyPath: String(idItem),
    icon: (
      <NavIcon
        icon={
          'options' in dbTenderFolder ? FolderTenderHammer : FileTenderHammer
        }
      />
    ),
    children: [],
    isTender: true,
    parentId: String(root_folder_id || idItem),
  };
};

export const arrToTreeTenderFolder2 = (
  arr: DbTenderFolder[],
  parentId: DbTenderFolder['options']['root_folder_id']
): StateDataNode[] => {
  return arr
    .filter(
      (dbTenderFolder) =>
        dbTenderFolder.options.root_folder_id === parentId &&
        !dbTenderFolder.options.tender_registration_id
    )
    .map((dbTenderFolder) => ({
      ...dbTenderFolderToTreeObj(dbTenderFolder),
      children: arrToTreeTenderFolder(arr, dbTenderFolder.id),
      description: dbTenderFolder.description,
      title: dbTenderFolder.name,
      isTender: true,
      className: 'ant-table-row-selected',
    }));
};

export const arrToTreetendersChildren = (
  arr: DbTenderFolder[]
): StateDataNode[] => {
  return arr.map((dbTenderFolder) => ({
    ...dbTendersChildrenToTreeObj(dbTenderFolder),
    //children: arrToTreeTenderFolder(arr, dbTenderFolder.folder_id),
    description: dbTenderFolder.description,
    title: dbTenderFolder.name,
    isTender: true,
    className: 'ant-table-row-selected',
  }));
};

export const arrToTreeTenders2 = (
  arr: DbTender[],
  parentId: DbTenderFolder['options']['root_folder_id'],
  tenderFoldersArr: DbTenderFolder[]
): StateDataNode[] => {
  return arr
    .filter((dbTenderFolder) => dbTenderFolder.folder_id === parentId)
    .map((dbTenderFolder) => ({
      ...dbTendersChildrenToTreeObj(dbTenderFolder),
      children: dbTenderFolder?.tender_root_folders
        ? arrToTreetendersChildren(dbTenderFolder?.tender_root_folders)
        : [],
      description: dbTenderFolder.description,
      title: dbTenderFolder.title,
      isTender: true,
    }));
};

// Convert sector tree to DataNode format for navigation
const convertSectorTreeToDataNodes = (
  sectorTree: Block[],
  intl: any,
  projectId: string | number,
  iconStyle: any
): StateDataNode[] => {
  // Add "Outside sectors" as first entry
  const outsideSectorsNode: StateDataNode = {
    key: 'sector-outside',
    title: intl.formatMessage({ id: 'outside-sectors' }),
    icon: (
      <IconButton
        icon={['fad', 'map-location'] as IconProp}
        color="blue"
        customStyle={iconStyle}
        backgroundOff
      />
    ),
    isLeaf: true,
    path: `/projects/${projectId}/section-modules/sector/null/registrations`,
    parentId: null,
  };

  const sectorNodes = sectorTree.map((block) => ({
    key: `block-${block.block_id}`,
    title: block.block_name,
    icon: (
      <IconButton
        icon={['fad', 'layer-group'] as IconProp}
        color="blue"
        customStyle={iconStyle}
        backgroundOff
      />
    ),
    isLeaf: false,
    parentId: null,
    // No path - divisions shouldn't navigate, only expand/collapse
    children: block.subdivisions.map((subdivision) => ({
      key: `subdivision-${subdivision.subdivision_id}`,
      title: subdivision.subdivision_name,
      icon: (
        <NavIcon
          icon={LayerCustom}
          customStyle={{ height: '20px', width: 'auto' }}
        />
      ),
      isLeaf: false,
      parentId: `block-${block.block_id}`,
      // No path - subdivisions shouldn't navigate, only expand/collapse
      children: subdivision.sectors.map((sector) => ({
        key: `sector-${sector.sector_id}`,
        title: sector.alt_name || sector.sector_name,
        displayTitle: sector.sector_name,
        icon: (
          <IconButton
            icon={['fad', 'map-location-dot'] as IconProp}
            color="blue"
            customStyle={iconStyle}
            backgroundOff
          />
        ),
        isLeaf: true,
        path: `/projects/${projectId}/section-modules/sector/${sector.sector_id}/registrations`,
        parentId: `subdivision-${subdivision.subdivision_id}`,
      })),
    })),
  }));

  return [outsideSectorsNode, ...sectorNodes];
};

const arrToTreeTenderFolder = (data: any[], parent: any) => {
  // if (!data[0]?.id) return;
  return data.map((item) => {
    //const children = arrToTreeTenderFolder(item, item);
    return {
      title: item.options.alias_title,
      icon: <NavIcon icon={FolderTenderHammer} />,
      key: String(item.id) + '_tender_folders',
      type: 'tender-folders',
      keyPath: String(item.id),
      //children,
      /*    children:
          item.options && item.options.sub_folder_ids.length > 0
            ? arrToTreeTenderFolder(item.options.sub_folder_ids, item)
            : [], */
      isTender: true,
      isUnassigned: true,
      path: `/tender-folders/${item.id}/folders/${item.id}`,
      className: 'ant-table-row-selected',
      tenderRegistrationId: item.options.tender_registration_id,
    };
  });
};

const arrToTreeTenders = (
  data: any[],
  parent: any,
  tenderFoldersData: any[]
) => {
  return data.map((item) => {
    const registrationFolders = tenderFoldersData.filter(
      (folder) => folder?.options?.tender_registration_id === item.id
    );

    return {
      title: item.title,
      icon: <NavIcon icon={FileTenderHammer} />,
      key: String(item.id) + '_tender_registartions',
      keyPath: String(item.id),
      type: 'tender_registartions',
      isTender: true,
      className: 'ant-table-row-selected',
      children: arrToTreeTendersSub(registrationFolders, null, String(item.id)),
    };
  });
};

interface SubFolder {
  id: number;
  options: {
    alias_title: string | null;
    is_tender_root_folder: boolean;
    tender_registration_id: number;
    root_folder_id: number | null;
    sub_folder_ids: number[] | null;
  };
  project_id: number;
  parent_id: number | null;
  state: number;
  name: string;
  description: string | null;
  created_by: number;
  created_at: number;
  updated_by: number;
  updated_at: number;
  module: number;
  permission: number;
}

const arrToTreeTenderFolderSub = (
  data: { [key: string]: SubFolder },
  parent: SubFolder | null,
  tenderId?: string
): any[] => {
  return Object.values(data)
    .filter((item) => item.parent_id === (parent ? parent.id : null))
    .map((item) => ({
      title: item.name,
      icon: (
        <NavIcon
          icon={getTenderIconArichive({
            is_tender_root_folder: true,
            is_sub_folder: true,
          })}
        />
      ),
      key: `${item.id}_tender_folders`,
      isTenderRootFolder: item.options.is_tender_root_folder,
      tenderRegistrationId: item.options.tender_registration_id || tenderId,
      type: item.options.is_tender_root_folder
        ? 'tender_registartions'
        : 'tender-folders',
      keyPath: String(item.id),
      /*     children: item.options.sub_folder_ids
        ? arrToTreeTenderFolderSub(data, item, tenderId)
        : [], */
      isTender: true,
      className: 'ant-table-row-selected',
      path: `/tenders/${
        item.options?.tender_registration_id || tenderId
      }/documents/tender-folders/${
        item.options?.is_tender_root_folder ? item.id : item.parent_id
      }/folders/${item.id}`,
    }));
};

const arrToTreeTenderFolderSubNoLoad = (
  data: { [key: string]: SubFolder } | SubFolder[],
  parent: SubFolder | null,
  tenderId?: string,
  firstElementId?: string
): any[] => {
  const items = Array.isArray(data) ? data : Object.values(data);

  // id кореневої tender-папки (C08 ...) — використовується в path
  const rootId = firstElementId || (items[0] ? String(items[0].id) : undefined);

  return items
    .filter((item) => item.parent_id === (parent ? parent.id : null))
    .map((item) => {
      const children = arrToTreeTenderFolderSubNoLoad(
        data,
        item,
        tenderId,
        rootId
      );

      return {
        title: item.name,
        icon: <NavIcon icon={FolderTender} />,
        key: `${item.id}_tender_folders`,
        isTenderRootFolder: item.options.is_tender_root_folder,
        tenderRegistrationId: item.options.tender_registration_id || tenderId,
        type: item.options.is_tender_root_folder
          ? 'tender_registartions'
          : 'tender-folders',
        keyPath: String(item.id),
        children,
        isTender: true,
        className: 'ant-table-row-selected',
        path: `/tenders/${
          item.options?.tender_registration_id || tenderId
        }/documents/tender-folders/${rootId}/folders/${item.id}`,
        firstElementId: rootId,
        isLeaf: children.length === 0,
      };
    });
};

const arrToTreeTenderFolderSubUnnasignedNoLoad = (
  data: { [key: string]: SubFolder },
  parent: SubFolder | null,
  tenderId?: string,
  firstElementId?: string
): any[] => {
  return Object.values(data)
    .filter((item) => item.parent_id === (parent ? parent.id : null))
    .map((item) => {
      const children = arrToTreeTenderFolderSubUnnasignedNoLoad(
        data,
        item,
        tenderId,
        firstElementId
      );
      return {
        title: item.name,
        icon: <NavIcon icon={FolderTender} />,
        key: `${item.id}_tender_folders`,
        isTenderRootFolder: item.options.is_tender_root_folder,
        tenderRegistrationId: item.options.tender_registration_id,
        type: 'tender-folders',
        keyPath: String(item.id),
        children,
        isTender: true,
        className: 'ant-table-row-selected',
        path: `/tender-folders/${firstElementId}/folders/${item.id}`,

        firstElementId,
        isLeaf: children.length === 0,
      };
    });
};

const arrToTreeTendersSub = (
  data: { [key: string]: SubFolder },
  parent: SubFolder | null,
  tenderId?: string
): any[] => {
  return Object.values(data)
    .filter(
      (item) => item.options.root_folder_id === (parent ? parent.id : null)
    )
    .map((item) => ({
      title: item.options.alias_title,
      icon: <NavIcon icon={FolderTenderHammer} />,
      key: `${item.id}_tender_registartions`,
      keyPath: String(item.id),
      isTenderRootFolder: item.options.is_tender_root_folder,
      tenderRegistrationId: item.options.tender_registration_id,
      path: `/tenders/${
        item.options?.tender_registration_id
      }/documents/tender-folders/${
        item.options?.is_tender_root_folder ? item.id : item.parent_id
      }/folders/${item.id}`,
      type:
        item.options.is_tender_root_folder || tenderId
          ? 'tender_registartions'
          : 'tender-folders',
      children: item.options.sub_folder_ids
        ? arrToTreeTenderFolderSub(data, item, tenderId)
        : [],
      isTender: true,
      className: 'ant-table-row-selected',
    }));
};
